import React from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAccommodationsCta } from '../../hooks/useAccommodationsCta';
import { usePartnerHotelRedirect } from '../../hooks/usePartnerHotelRedirect';
import { RedirectWithSafeLink } from '@apps/guest/components/RedirectWithSafeLink';
import { RedirectInterceptorDialog } from '@apps/guest/routes/HotelBooking/components/RedirectInterceptorDialog';
import { AccommodationCombined } from '../Accommodations/Accommodation.types';
import { EventVenue } from '@shared/utils/eventPlace';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

interface WrapPartnerHotelRedirectProps {
  locationInfo?: EventVenue;
  eventHandle: string;
  accommodation: AccommodationCombined;
}

/**
 * HOC that provides redirect and selection logic to a child component.
 * @param WrappedComponent - The component to wrap and inject `onSelectAccommodation`
 */
export const wrapPartnerHotelRedirect = <P extends object>(
  WrappedComponent: React.ComponentType<P & { onSelectAccommodation: (event: React.MouseEvent<HTMLAnchorElement>) => void }>
) => {
  return (props: P & WrapPartnerHotelRedirectProps) => {
    const { eventHandle, accommodation, locationInfo } = props;
    const { id, type } = accommodation;

    const [accommodationsGuestRedirectImprovementsEnabled] = useIsFeatureEnabled('accommodationsGuestRedirectImprovementsEnabled');
    const { handleOnSelectAccommodation } = useAccommodationsCta({ eventHandle, accommodation, locationInfo });
    const { pricingInfo, safeLink, isOpenInterceptorDialog, setSafeLink, partnerHotelRedirect, handleOnAccept, handleOnDecline } = usePartnerHotelRedirect(
      id,
      accommodation?.bookingUrl || ''
    );

    const onSelectAccommodation = useEventCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
      handleOnSelectAccommodation();
      if (accommodationsGuestRedirectImprovementsEnabled) {
        partnerHotelRedirect(event, type);
      }
    });

    return (
      <>
        <WrappedComponent {...props} onSelectAccommodation={onSelectAccommodation} />
        <RedirectWithSafeLink href={safeLink} skipTelemetry target="_blank" onClose={() => setSafeLink(undefined)} />
        <RedirectInterceptorDialog
          isOpen={isOpenInterceptorDialog}
          hotel={{
            hotelName: accommodation.displayName,
            hotelPriceCurrency: pricingInfo?.nightlyBaseRate.currency,
            hotelPrice: pricingInfo?.nightlyBaseRate.floatingPointDecimalString,
            hotelStrikeOutPrice: pricingInfo?.strikeoutNightlyBaseRate?.floatingPointDecimalString
          }}
          onAccept={handleOnAccept}
          onDecline={handleOnDecline}
        />
      </>
    );
  };
};
